import { createFileRoute, redirect, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocaleFmt } from "@/lib/i18n/format";
import { supabase } from "@/integrations/supabase/client";
import { PageHeader, PageBody } from "@/components/app/page-header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { AlertTriangle, Download, RefreshCw, Bug } from "lucide-react";

export const Route = createFileRoute("/_authenticated/app/admin/errors")({
  beforeLoad: async () => {
    const { data: u } = await supabase.auth.getUser();
    if (!u.user) throw redirect({ to: "/login" });
    const { data: roles } = await supabase.from("user_roles").select("role").eq("user_id", u.user.id);
    if (!roles?.some((r) => r.role === "admin")) throw redirect({ to: "/app" });
  },
  component: ErrorsDashboard,
});

type ErrRow = {
  id: string;
  level: "error" | "warn" | "info";
  source: string;
  message: string;
  stack: string | null;
  route: string | null;
  user_id: string | null;
  context: any;
  created_at: string;
};

function ErrorsDashboard() {
  const { t } = useTranslation();
  const { localeTag } = useLocaleFmt();
  const [rows, setRows] = useState<ErrRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [level, setLevel] = useState<string>("all");
  const [source, setSource] = useState<string>("all");
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    const { data } = await supabase
      .from("error_logs" as any)
      .select("*")
      .order("created_at", { ascending: false })
      .limit(500);
    setRows((data ?? []) as unknown as ErrRow[]);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const sources = useMemo(() => Array.from(new Set(rows.map((r) => r.source))).sort(), [rows]);

  const filtered = rows.filter((r) => {
    if (level !== "all" && r.level !== level) return false;
    if (source !== "all" && r.source !== source) return false;
    if (q && !(`${r.message} ${r.route ?? ""} ${r.source}`.toLowerCase().includes(q.toLowerCase()))) return false;
    return true;
  });

  const now = Date.now();
  const day = 86400000;
  const last24 = rows.filter((r) => now - new Date(r.created_at).getTime() < day);
  const last7 = rows.filter((r) => now - new Date(r.created_at).getTime() < 7 * day);
  const quotaEvents = last24.filter((r) => r.source === "quota").length;
  const uniqueUsers = new Set(last7.map((r) => r.user_id).filter(Boolean)).size;

  // Daily series, last 14 days, by source group
  const series = useMemo(() => {
    const map = new Map<string, any>();
    for (let i = 13; i >= 0; i--) {
      const d = new Date(now - i * day);
      const key = d.toISOString().slice(0, 10);
      map.set(key, { day: key.slice(5), client: 0, server: 0, ai: 0, quota: 0, ocr: 0, drafting: 0, other: 0 });
    }
    for (const r of rows) {
      const key = r.created_at.slice(0, 10);
      const o = map.get(key);
      if (!o) continue;
      const cat = r.source.startsWith("router") || r.source.startsWith("window") || r.source.startsWith("unhandled")
        ? "client"
        : r.source.startsWith("chat") ? "server"
        : r.source === "ai" ? "ai"
        : r.source === "quota" ? "quota"
        : r.source === "ocr" ? "ocr"
        : r.source.startsWith("drafting") ? "drafting"
        : "other";
      o[cat] = (o[cat] ?? 0) + 1;
    }
    return Array.from(map.values());
  }, [rows]);

  const topMessages = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of last7) m.set(r.message, (m.get(r.message) ?? 0) + 1);
    return Array.from(m.entries()).sort((a, b) => b[1] - a[1]).slice(0, 8);
  }, [last7]);

  const exportCsv = () => {
    const header = "created_at,level,source,route,user_id,message\n";
    const body = filtered.map((r) =>
      [r.created_at, r.level, r.source, r.route ?? "", r.user_id ?? "", JSON.stringify(r.message)].join(",")
    ).join("\n");
    const blob = new Blob([header + body], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `error-logs-${new Date().toISOString().slice(0,10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <PageHeader
        title={t("pages.admin.errorsPage.title")}
        description={t("pages.admin.errorsPage.description")}
        actions={
          <div className="flex items-center gap-2">
            <Button asChild variant="outline" size="sm"><Link to="/app/admin">{t("pages.admin.adminBack")}</Link></Button>
            <Button variant="outline" size="sm" onClick={load}><RefreshCw className="mr-2 h-4 w-4" /> {t("pages.admin.reload")}</Button>
            <Button size="sm" onClick={exportCsv}><Download className="mr-2 h-4 w-4" /> {t("pages.admin.errorsPage.exportCsv")}</Button>
          </div>
        }
      />
      <PageBody>
        <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[
            { label: t("pages.admin.errorsPage.events24h"), value: last24.length, icon: AlertTriangle },
            { label: t("pages.admin.errorsPage.events7d"), value: last7.length, icon: Bug },
            { label: t("pages.admin.errorsPage.usersImpacted"), value: uniqueUsers, icon: Bug },
            { label: t("pages.admin.errorsPage.quotaExhausted"), value: quotaEvents, icon: AlertTriangle },
          ].map((s) => (
            <Card key={s.label}>
              <CardContent className="flex items-center gap-3 pt-6">
                <s.icon className="h-5 w-5 text-muted-foreground" />
                <div>
                  <div className="text-2xl font-bold">{s.value}</div>
                  <div className="text-xs text-muted-foreground">{s.label}</div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <Card className="mb-6">
          <CardHeader><CardTitle>{t("pages.admin.errorsPage.trend14d")}</CardTitle></CardHeader>
          <CardContent style={{ height: 280 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={series}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                <XAxis dataKey="day" stroke="#888" fontSize={11} />
                <YAxis stroke="#888" fontSize={11} allowDecimals={false} />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="client" stroke="#dc2626" dot={false} />
                <Line type="monotone" dataKey="server" stroke="#2563eb" dot={false} />
                <Line type="monotone" dataKey="ai" stroke="#7c3aed" dot={false} />
                <Line type="monotone" dataKey="quota" stroke="#d97706" dot={false} />
                <Line type="monotone" dataKey="ocr" stroke="#059669" dot={false} />
                <Line type="monotone" dataKey="drafting" stroke="#0891b2" dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <div className="grid gap-6 lg:grid-cols-[1fr_2fr]">
          <Card>
            <CardHeader><CardTitle>{t("pages.admin.errorsPage.topMessages")}</CardTitle></CardHeader>
            <CardContent>
              {topMessages.length === 0 ? (
                <p className="text-sm text-muted-foreground">{t("pages.admin.errorsPage.noErrors")}</p>
              ) : (
                <ul className="space-y-2">
                  {topMessages.map(([m, n]) => (
                    <li key={m} className="flex items-start justify-between gap-2 rounded-md border p-2 text-sm">
                      <span className="line-clamp-2">{m}</span>
                      <Badge variant="secondary">{n}</Badge>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex flex-wrap items-center justify-between gap-2">
                <span>{t("pages.admin.errorsPage.recent")}</span>
                <div className="flex flex-wrap items-center gap-2">
                  <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("pages.admin.search")} className="h-8 w-48" />
                  <Select value={level} onValueChange={setLevel}>
                    <SelectTrigger className="h-8 w-32"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t("pages.admin.errorsPage.allLevels")}</SelectItem>
                      <SelectItem value="error">error</SelectItem>
                      <SelectItem value="warn">warn</SelectItem>
                      <SelectItem value="info">info</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={source} onValueChange={setSource}>
                    <SelectTrigger className="h-8 w-40"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t("pages.admin.errorsPage.allSources")}</SelectItem>
                      {sources.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <p className="text-sm text-muted-foreground">{t("pages.admin.loading")}</p>
              ) : (
                <div className="max-h-[60vh] overflow-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t("pages.admin.cols.when")}</TableHead>
                        <TableHead>{t("pages.admin.cols.level")}</TableHead>
                        <TableHead>{t("pages.admin.cols.source")}</TableHead>
                        <TableHead>{t("pages.admin.cols.message")}</TableHead>
                        <TableHead>{t("pages.admin.cols.route")}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filtered.slice(0, 200).map((r) => (
                        <TableRow key={r.id}>
                          <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                            {new Date(r.created_at).toLocaleString(localeTag)}
                          </TableCell>
                          <TableCell>
                            <Badge variant={r.level === "error" ? "destructive" : r.level === "warn" ? "default" : "secondary"}>
                              {r.level}
                            </Badge>
                          </TableCell>
                          <TableCell className="font-mono text-xs">{r.source}</TableCell>
                          <TableCell className="max-w-[480px]">
                            <div className="line-clamp-2 text-xs">{r.message}</div>
                            {r.stack && <div className="mt-1 line-clamp-1 font-mono text-[10px] text-muted-foreground">{r.stack.split("\n")[0]}</div>}
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">{r.route ?? "-"}</TableCell>
                        </TableRow>
                      ))}
                      {filtered.length === 0 && (
                        <TableRow><TableCell colSpan={5} className="py-8 text-center text-sm text-muted-foreground">{t("pages.admin.noEvents")}</TableCell></TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </PageBody>
    </>
  );
}
